import { useRef, useState, type ReactNode } from 'react'
import { Icone } from '@/components/ui/Icone'

/**
 * Tirer vers le bas pour rafraîchir — RG-2.13ter.
 *
 * **C'est le seul geste qui recompose le Fil.** Changer d'onglet le conserve
 * (RG-2.13bis) : aller voir une archive une seconde ne doit pas coûter quinze
 * cartes de travail. Reste qu'il faut bien pouvoir demander du neuf, et ce
 * geste-là, le candidat le connaît de toutes les applications qu'il utilise.
 *
 * **Pourquoi pas un bouton.** Il coûterait 44 points de haut sur un écran où
 * chaque ligne compte, il serait tapé par erreur en défilant, et il occuperait
 * la place au moment où l'on n'en a pas besoin — c'est-à-dire tout le temps
 * sauf une fois par séance.
 *
 * **Il ne se déclenche qu'au SOMMET.** Tirer au milieu du fil est un
 * défilement ordinaire ; y attacher un rafraîchissement ferait perdre sa place
 * à chaque geste un peu vif.
 *
 * **Aucune bibliothèque.** Trois écouteurs tactiles et une translation. Un
 * paquet pour cela pèserait plus lourd que le budget entier de la page (05 §8).
 */

/** La distance à tirer pour déclencher. Plus court, on rafraîchit par accident. */
const SEUIL = 72

/** Au-delà, le geste ne suit plus le doigt : on résiste, comme un élastique. */
const MAX = 110

export function TirerPourRafraichir({
  onRafraichir,
  enCours,
  children,
}: {
  onRafraichir: () => void
  enCours: boolean
  children: ReactNode
}) {
  const [tire, setTire] = useState(0)
  const depart = useRef<number | null>(null)

  const auSommet = (): boolean =>
    // `document.scrollingElement` plutôt que `window.scrollY` : la coque
    // défile dans son propre conteneur sur grand écran.
    (document.scrollingElement?.scrollTop ?? window.scrollY) <= 0

  return (
    <div
      onTouchStart={(e) => {
        depart.current = auSommet() ? (e.touches[0]?.clientY ?? null) : null
      }}
      onTouchMove={(e) => {
        if (depart.current === null || enCours) return

        const delta = (e.touches[0]?.clientY ?? 0) - depart.current

        if (delta <= 0) {
          setTire(0)

          return
        }

        // Racine carrée : le mouvement suit le doigt au début puis résiste,
        // ce qui dit « c'est assez » sans mot ni butée brutale.
        setTire(Math.min(MAX, Math.sqrt(delta) * 7))
      }}
      onTouchEnd={() => {
        if (tire >= SEUIL && !enCours) onRafraichir()

        depart.current = null
        setTire(0)
      }}
    >
      {/* La zone qui s'ouvre au-dessus du fil. Hauteur pilotée par le geste :
          rien n'apparaît tant qu'on n'a pas tiré. */}
      <div
        className="flex items-center justify-center overflow-hidden transition-[height]"
        style={{
          height: enCours ? 44 : tire,
          color: 'var(--texte-2)',
          // Pas de transition PENDANT le geste : elle ferait traîner l'écran
          // derrière le doigt.
          transitionDuration: tire > 0 ? '0ms' : '200ms',
        }}
      >
        <span
          className="flex items-center gap-2 text-[13px]"
          style={{
            // L'icône tourne avec le geste, puis en continu pendant l'appel :
            // c'est ce qui distingue « je tire » de « ça charge ».
            transform: enCours ? undefined : `rotate(${String(tire * 2.4)}deg)`,
            animation: enCours ? 'upfac-tourne 900ms linear infinite' : undefined,
          }}
        >
          <Icone nom="fleche-droite" taille={18} />
        </span>

        {!enCours && tire >= SEUIL && (
          <span className="ml-2 text-[13px] font-bold">Relâchez pour actualiser</span>
        )}
      </div>

      {children}
    </div>
  )
}
